import { Grid, Typography, Box } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { mapVehiclesAssets } from 'utils/helpers/mapAssetImage';
import { SVG } from 'components/Asset/SVG';
import type { DeviceStats } from 'types/utilization.types';
import { truncateTo2dp } from 'utils/helpers/general';
import {
  convertMinutesToHours,
  convertsecondsToHours,
  getShiftHours,
} from 'views/Utilization/utils';
import { type Asset } from 'types/asset.types';
import { useAppSelector } from 'store/hook';
import { IgnitionStatusIndicator } from 'components/StatusIndicator';
import useAppTheme from 'hooks/useAppTheme';
import { addLiveDataToAssets } from 'views/FleetOverview/helpers/asset.helpers';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  data: DeviceStats;
  utilizationType: string;
}

const AssetItemDetails = ({ data, utilizationType }: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { latestTagData } = useAppSelector((state) => state.deviceReducer);
  // const { customerCode } = useCustomerInfo();
  const { assets } = useAppSelector((state) => state.assetReducer);
  const assetsWithLiveData = addLiveDataToAssets(assets, latestTagData);
  // const noOfValidEntries = Object.values(data.date_stats).filter(
  //   (entry) => entry.utilization_engine_active_minute > 0
  // ).length;

  // const shiftHours =
  //   noOfValidEntries > 0
  //     ? getShiftHours(customerCode) *
  //       (noOfValidEntries - 1 > 0 ? noOfValidEntries - 1 : 1)
  //     : 0;

  return (
    <Box
      sx={{
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <SVG
        name={mapVehiclesAssets(data.asset_type)}
        style={{
          fill: '#E2C171',
          height: '30px',
          width: '60px',
          paddingRight: '10px',
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={3}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '13px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'left',
                }}
              >
                <IgnitionStatusIndicator
                  latestTagData={latestTagData}
                  asset={
                    assetsWithLiveData.find(
                      (asset) => asset.bumperNumber === data.bumper_id
                    ) as Asset
                  }
                  size={10}
                  marginRight="5px"
                />
                {data.bumper_id}
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* <PowerSettingsNewIcon
                sx={{
                  color: '#00C853',
                  marginRight: '8px',
                  fontSize: '12px',
                }}
              /> */}
              <Typography
                sx={{
                  fontSize: '10px',
                  color: palette.textPrimary,
                }}
              >
                {data.make} - {data.model}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
                textAlign: 'center',
              }}
            >
              {t('utilization')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
                textAlign: 'center',
              }}
            >
              {truncateTo2dp(
                Number(data?.avg_utilization_by_shift_hours) * 100
              )}
              %
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
                textAlign: 'center',
              }}
            >
              {t('productive_hours')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
                textAlign: 'center',
              }}
            >
              {truncateTo2dp(Number(data?.total_engine_working_minutes) / 60)}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
                textAlign: 'center',
              }}
            >
              {t('idling_hours')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
                textAlign: 'center',
              }}
            >
              {truncateTo2dp(
                convertsecondsToHours(Number(data?.total_idling_duration))
              )}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
                textAlign: 'center',
              }}
            >
              {utilizationType === 'shiftHours'
                ? 'Shift Hours'
                : 'Engine Hours'}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
                textAlign: 'center',
              }}
            >
              {utilizationType === 'shiftHours'
                ? truncateTo2dp(
                    Number(convertMinutesToHours(Number(data?.totalShiftHours)))
                  )
                : truncateTo2dp(
                    Number(
                      convertMinutesToHours(
                        Number(data?.total_engine_active_minutes)
                      )
                    )
                  )}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AssetItemDetails;
